import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getClient, resolveAuthEnv, configuredEnvs, getProjectInfo } from '../lib/supabase';
import type { Env } from '../lib/env';
import { pendingOAuthResume } from './OAuthConsent';

/**
 * Handles Supabase auth redirects — magic links, password resets, email confirmations.
 * Supabase appends tokens as URL hash fragments: #access_token=...&type=recovery
 * This page reads those fragments, establishes the session, then either redirects
 * into the app or — for a recovery link — offers a set-password form.
 *
 * TWO BUGS FIXED HERE 2026-08-23, both found by a founder trying to reach Stage
 * through the newly-deployed admin. They are worth stating because both produced
 * symptoms that read as "my account is broken" when nothing was wrong with it.
 *
 * 1. ENV CAME FROM MEMORY, NOT FROM THE TOKEN.
 *    `?env=` is stamped by Login.tsx onto links THIS app mints. A link minted
 *    anywhere else — notably Authentication → Users → "Send password recovery"
 *    in the Supabase dashboard — carries no such parameter. The callback then
 *    fell back to the REMEMBERED env, which is a guess, and the guess was wrong:
 *    a Stage recovery link dropped the operator on the Proto dashboard while
 *    Stage had server-side minted a session the app then filed nowhere. Select
 *    Stage → no local session → back to login. A loop with no error message.
 *
 *    The token itself is the authority and always has been. A Supabase JWT's
 *    `iss` claim is `https://<ref>.supabase.co/auth/v1`, and `<ref>` identifies
 *    the project unambiguously. So the env is now DERIVED from the token and
 *    `?env=` is a hint used only when no token is readable (the PKCE `?code=`
 *    flow, where there is nothing to decode until after the exchange).
 *
 * 2. RECOVERY LINKS SIGNED YOU IN AND DISCARDED THE POINT OF THE LINK.
 *    `type=recovery` was never inspected. The token was consumed as a session
 *    and the callback navigated to the dashboard, so the one moment Supabase
 *    grants for setting a password passed silently. The practical consequence
 *    was that an admin password could ONLY ever be set out-of-band, which is
 *    how a project ends up with a live credential nobody has a record of.
 *
 * Skipping is allowed on purpose. The session is already valid by the time the
 * form renders, so trapping someone behind a password they did not ask to set
 * would be a worse failure than the one being fixed.
 */

/** The project a token was minted by, or null if it cannot be read.
 *  Decoding is deliberately tolerant: a malformed token is not an identity
 *  claim, it is an absence of one, and the caller falls back rather than
 *  guessing from a half-parsed value. */
function envFromToken(accessToken: string | null): Env | null {
  if (!accessToken) return null;
  try {
    const part = accessToken.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const iss = String((JSON.parse(json) as { iss?: unknown }).iss ?? '');
    const ref = iss.match(/https?:\/\/([a-z0-9]+)\.supabase\.co/i)?.[1];
    if (!ref) return null;
    for (const e of configuredEnvs()) {
      if (getProjectInfo(e).ref === ref) return e;
    }
  } catch {
    // Unreadable token — fall through to the caller's fallback.
  }
  return null;
}

export function AuthCallback() {
  const navigate = useNavigate();
  const loc = useLocation();

  const hashParams   = new URLSearchParams(window.location.hash.replace('#', ''));
  const searchParams = new URLSearchParams(window.location.search);
  const accessToken  = hashParams.get('access_token');
  const isRecovery   = (hashParams.get('type') ?? searchParams.get('type')) === 'recovery';

  // The token wins. `?env=` and the remembered choice are fallbacks for the
  // PKCE flow, where nothing is decodable until the code has been exchanged.
  const env: Env = envFromToken(accessToken)
    ?? resolveAuthEnv(loc.pathname, loc.search || window.location.search);
  const home = `/${env}/dashboard`;
  const project = getProjectInfo(env);

  const [status, setStatus] = useState<'processing' | 'error' | 'set_password'>('processing');
  const [errorMsg, setErrorMsg] = useState('');
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function handleCallback() {
      const supabase = getClient(env);
      if (!supabase) {
        setStatus('error');
        setErrorMsg(`This build cannot reach ${env}. Its URL or key is missing.`);
        return;
      }

      let session = null;
      let error = null;

      const code = searchParams.get('code');
      if (code) {
        const result = await supabase.auth.exchangeCodeForSession(code);
        session = result.data.session;
        error   = result.error;
      } else {
        const refreshToken = hashParams.get('refresh_token');
        if (accessToken && refreshToken) {
          const result = await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
          session = result.data.session;
          error   = result.error;
        } else {
          const result = await supabase.auth.getSession();
          session = result.data.session;
          error   = result.error;
        }
      }

      if (error || !session) {
        setStatus('error');
        setErrorMsg(error?.message ?? 'Session could not be established. The link may have expired.');
        return;
      }

      // api_admin.whoami() — the caller's own admin row or none; answers at aal1 (CS-5).
      const { data: profile } = await supabase
        .schema('api_admin')
        .rpc('whoami')
        .single<{ id: string; is_active: boolean }>();

      if (!profile || !profile.is_active) {
        await supabase.auth.signOut();
        setStatus('error');
        setErrorMsg('Account not found or inactive. Contact your administrator.');
        return;
      }

      if (isRecovery) { setStatus('set_password'); return; }
      navigate(pendingOAuthResume() ?? home, { replace: true });
    }

    handleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, env, home]);

  async function savePassword(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg('');
    if (pw1.length < 12) { setErrorMsg('Use at least 12 characters.'); return; }
    if (pw1 !== pw2)     { setErrorMsg('The two entries do not match.'); return; }
    const supabase = getClient(env);
    if (!supabase) { setErrorMsg('Authentication service not configured.'); return; }
    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password: pw1 });
    setSaving(false);
    if (error) { setErrorMsg(error.message); return; }
    navigate(home, { replace: true });
  }

  return (
    <div className="min-h-screen bg-surface-light flex items-center justify-center">
      <div className="bg-white border border-border rounded-2xl shadow-sm px-8 py-10 w-full max-w-sm text-center">
        <div className="w-10 h-10 rounded-xl bg-brand-orange flex items-center justify-center mx-auto mb-4">
          <span className="text-white text-base font-bold">V</span>
        </div>

        {status === 'processing' && (
          <>
            <h1 className="text-base font-bold text-near-black mb-2">Signing you in…</h1>
            <p className="text-sm text-gray-text">Just a moment.</p>
            <div className="mt-6 flex justify-center">
              <div className="w-5 h-5 border-2 border-brand-orange border-t-transparent rounded-full animate-spin" />
            </div>
          </>
        )}

        {status === 'set_password' && (
          <form onSubmit={savePassword} className="text-left">
            <h1 className="text-base font-bold text-near-black mb-1 text-center">Set a password</h1>
            {/* Which project, stated. Sessions and users are per-project, and a
                password set against the wrong one is invisible until it fails. */}
            <p className="text-xs text-gray-text mb-4 text-center">
              for <span className="font-semibold">{project.name}</span>{project.ref ? ` (${project.ref})` : ''}
            </p>
            <input
              type="password" autoComplete="new-password" placeholder="New password"
              value={pw1} onChange={e => setPw1(e.target.value)} disabled={saving}
              className="w-full mb-2 px-3 py-2 border border-border rounded-xl text-sm disabled:opacity-50"
            />
            <input
              type="password" autoComplete="new-password" placeholder="Confirm password"
              value={pw2} onChange={e => setPw2(e.target.value)} disabled={saving}
              className="w-full mb-3 px-3 py-2 border border-border rounded-xl text-sm disabled:opacity-50"
            />
            {errorMsg && <p className="text-sm text-red-700 mb-3">{errorMsg}</p>}
            <button
              type="submit" disabled={saving}
              className="w-full py-2.5 rounded-xl bg-brand-orange text-white text-sm font-semibold hover:bg-brand-orange/90 transition disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Set password'}
            </button>
            {/* You are already signed in by this point. Being unable to leave
                would be a worse trap than the missing form this replaced. */}
            <button
              type="button" onClick={() => navigate(home, { replace: true })}
              className="w-full mt-2 py-2 text-sm text-gray-text hover:text-near-black transition"
            >
              Skip for now
            </button>
          </form>
        )}

        {status === 'error' && (
          <>
            <h1 className="text-base font-bold text-near-black mb-2">Sign-in failed</h1>
            <p className="text-sm text-gray-text mb-6">{errorMsg}</p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="w-full py-2.5 rounded-xl bg-brand-orange text-white text-sm font-semibold hover:bg-brand-orange/90 transition"
            >
              Back to login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
